// src/components/ErrorBoundary.tsx — HP6: catches component crashes instead of blank screen
// In production: integrate Sentry.captureException(error) in componentDidCatch
import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface Props  { children: ReactNode }
interface State  { hasError: boolean; error: Error | null; info: ErrorInfo | null }

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, info: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, info: null }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // TODO Phase 2: Sentry.captureException(error, { extra: info })
    console.error('[ErrorBoundary] Unhandled component error:', error, info)
    this.setState({ info })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-red-200 rounded-2xl shadow-lg max-w-lg w-full p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-500 mb-4">
            An unexpected error occurred. The error has been logged. Try reloading the page or
            clicking below to recover.
          </p>
          {this.state.error && (
            <pre className="text-left text-xs bg-gray-100 rounded-lg px-4 py-3 text-gray-600 mb-5 overflow-auto max-h-32 border border-gray-200">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              Reload page
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            MCB Civil Electrical · TrafficProcure Beta ·{' '}
            {new Date().toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        </div>
      </div>
    )
  }
}
